import { useEffect, useMemo } from "react"
import axios from 'axios';
import * as turf from "@turf/turf";
import {
    Source,
    Layer,
    Popup
} from 'react-map-gl/maplibre';
import Table from 'react-bootstrap/Table';

const SlipWfsServer = axios.create({
    baseURL: "https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/Industry_and_Mining_WFS/MapServer/WFSServer",
    timeout: 30000
});

/**
 * Create popup for amalgamations pending tenement
 */
export function createAmalgamationsPendingPopup(feature, latLng, setClosed) {
    // Calculate area in hectares from the polygon
    const areaHa = (turf.area(feature) / 10000).toFixed(2);

    return (
        <Popup
            latitude={latLng.lat}
            longitude={latLng.lng}
            anchor="bottom"
            onClose={() => setClosed(false)}
        >
            <Table striped bordered style={{ marginBottom: 0 }}>
                <tbody>
                    <tr>
                        <td>Tenement ID:</td>
                        <td>{feature.properties.fmt_tenid}</td>
                    </tr>
                    <tr>
                        <td>Area:</td>
                        <td>{areaHa} HA</td>
                    </tr>
                    <tr>
                        <td>Amalgamation ID:</td>
                        <td>{feature.properties.amalgid}</td>
                    </tr>
                    <tr>
                        <td>Status:</td>
                        <td>Amalgamation Pending</td>
                    </tr>
                </tbody>
            </Table>
        </Popup>
    );
}

/**
 * Query the SLIP WFS server for amalgamations pending tenements
 * Fetches all features (no bbox filter) since there are only ~150-200 total
 */
export function queryAmalgamationsPendingWFS(setStateGeoJSON) {
    return SlipWfsServer.get("", {
        params: {
            service: 'WFS',
            version: '2.0.0',
            request: 'GetFeature',
            typeNames: 'esri:Tenements_-_Amalgamation_pending__DMIRS-031_',
            outputFormat: 'GEOJSON',
            srsName: 'EPSG:4326'
        }
    }).then((response) => {
        if (response.data.features && response.data.features.length > 0) {
            // Fix coordinate order - ESRI returns [lat, lng] but GeoJSON needs [lng, lat]
            const fixedFeatures = response.data.features.map(feature => {
                const fixCoordinates = (coords) => {
                    if (!coords) return coords;

                    // Check if this is a coordinate pair [lat, lng]
                    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                        return [coords[1], coords[0]]; // Swap to [lng, lat]
                    }

                    // Recursively fix nested arrays (for Polygons and MultiPolygons)
                    return coords.map(fixCoordinates);
                };

                return {
                    ...feature,
                    geometry: {
                        ...feature.geometry,
                        coordinates: fixCoordinates(feature.geometry.coordinates)
                    }
                };
            });

            const fixedData = {
                ...response.data,
                features: fixedFeatures
            };

            setStateGeoJSON(fixedData);
            return fixedData;
        }

        if (response.data.features !== undefined) {
            setStateGeoJSON(response.data);
        }
        return response.data;
    }).catch((error) => {
        console.error('Amalgamations Pending WFS query error:', error);
        // Return empty GeoJSON to keep UI functional
        const emptyResult = {
            type: "FeatureCollection",
            features: []
        };
        setStateGeoJSON(emptyResult);
        return emptyResult;
    });
}

/**
 * AmalgamationsPendingOverlay component
 * Displays tenements with amalgamation pending status from SLIP WFS
 */
export function AmalgamationsPendingOverlay({ overlayVisibility, selectedAmalgamation, geoJson, setGeoJson }) {
    // Fetch all amalgamations pending features once on mount
    useEffect(() => {
        if (!geoJson || geoJson.features.length === 0) {
            queryAmalgamationsPendingWFS(setGeoJson);
        }
    }, []); // Empty dependency array - fetch once on mount

    // Create point geometries for labels from polygon centroids (must be before early return)
    const labelsGeoJson = useMemo(() => {
        if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
            return {
                type: 'FeatureCollection',
                features: []
            };
        }

        const labelFeatures = geoJson.features.map(feature => {
            const centerOfMass = turf.centerOfMass(feature);
            return {
                type: 'Feature',
                geometry: centerOfMass.geometry,
                properties: feature.properties
            };
        });

        return {
            type: 'FeatureCollection',
            features: labelFeatures
        };
    }, [geoJson]);

    // Calculate visibility based on both tenements and amalgamationsPending checkboxes
    const isVisible = overlayVisibility.tenements && overlayVisibility.amalgamationsPending;

    // Don't render if no features loaded yet
    if (!geoJson.features || geoJson.features.length === 0) {
        return null;
    }

    return (
        <>
            <Source id="amalgamations-pending" type="geojson" data={geoJson}>
                <Layer
                    id='amalgamations-pending-fill'
                    type='fill'
                    paint={{ 'fill-color': '#0000FF', 'fill-opacity': 0.2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
                <Layer
                    id='amalgamations-pending-outline'
                    type='line'
                    paint={{ 'line-color': "#0000FF", 'line-width': 2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
                {/* Selected amalgamation highlight - yellow */}
                <Layer
                    id='selected-amalgamation-fill'
                    type='fill'
                    filter={['==', 'OBJECTID', selectedAmalgamation.properties.OBJECTID || 0]}
                    paint={{ 'fill-color': '#FFFF00', 'fill-opacity': 0.2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
                <Layer
                    id='selected-amalgamation-outline'
                    type='line'
                    filter={['==', 'OBJECTID', selectedAmalgamation.properties.OBJECTID || 0]}
                    paint={{ 'line-color': "#FFFF00", 'line-width': 2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
            </Source>

            {/* Separate source for labels using point geometries */}
            <Source id="amalgamations-pending-labels" type="geojson" data={labelsGeoJson}>
                <Layer
                    id='amalgamations-pending-labels'
                    minzoom={10.6}
                    type='symbol'
                    layout={{
                        'text-field': ['get', 'fmt_tenid'],
                        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                        'text-size': 12,
                        'text-anchor': 'center',
                        'text-allow-overlap': true,
                        'text-ignore-placement': true,
                        visibility: isVisible && overlayVisibility.tenementLabels ? 'visible' : 'none'
                    }}
                    paint={{
                        'text-color': 'rgba(100,150,255,1)',
                        'text-halo-color': 'rgba(0,0,0,1)',
                        'text-halo-width': 2
                    }}
                />
            </Source>
        </>
    );
}
